import { Component } from '@angular/core';
import {
  DwComponent,
  DwComponentType,
  DwDataSort,
  DwGridEvent,
  DwGridEventType,
  DwMetaDataFilterTileState,
  DwQueryFilterGroup
} from '@devwareapps/devware-cap';

import {
  AppMetaDataItemNames,
  UsersManageUsersCourseProgressGridQueryEntity
} from '../../../../meta-data/app-meta-data.service';
import {
  CourseProgressStatusChip
} from '../../../courses/components/cards/course-progress-status.util';
import { DwAviatorGridRendererBaseComponent } from '../../../shared/components/grids/dw-aviator-grid-renderer-base.component';

interface CourseProgressGridRowState {
  readonly fullName: string;
  readonly mediaPath: string;
  readonly progressPercent: number;
  readonly totalTimeLabel: string;
  readonly statusChips: CourseProgressStatusChip[];
}

@DwComponent({
  key: 'course-progress-grid-renderer',
  name: 'Course Progress Grid Renderer',
  isGlobal: false,
  parentItemName: AppMetaDataItemNames.DwUser,
  componentType: DwComponentType.gridRenderer
})
@Component({
  selector: 'app-course-progress-grid-renderer',
  templateUrl: './course-progress-grid-renderer.component.html',
  styleUrls: ['./course-progress-grid-renderer.component.scss']
})
export class CourseProgressGridRendererComponent
  extends DwAviatorGridRendererBaseComponent<UsersManageUsersCourseProgressGridQueryEntity> {

  public sortColumns = {
    user: '',
    course: '',
    started: '',
    lastActivity: '',
    status: '',
    totalTime: '',
    progress: '',
    acsScore: ''
  };
  public sortColumn = '';
  public sortDirection: 'asc' | 'desc' = 'asc';
  public rowStateById: { [id: number]: CourseProgressGridRowState } = {};

  setupRenderer(): void {
    this.resolveSortColumns();
    this.applyCurrentSort();
  }

  protected onGridConfigUpdated(): void {
    this.resolveSortColumns();
  }

  protected filterTileStateChange(_filterTileState: DwMetaDataFilterTileState): void {
    // This renderer does not add behavior to metadata filter tiles.
  }

  public customFiltersChanged(): void {
    this.applyCustomFilters(new DwQueryFilterGroup());
  }

  public toggleSort(column: string): void {
    if (!column) {
      return;
    }

    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = column === this.sortColumns.started || column === this.sortColumns.lastActivity
        ? 'desc'
        : 'asc';
    }

    this.applyCurrentSort();
  }

  public onRowClick(row: UsersManageUsersCourseProgressGridQueryEntity, event?: Event): void {
    this.setSelectedItems([row]);
    this.onGridEvent({
      eventType: DwGridEventType.rowSelectionChange,
      eventData: this.selectedItems,
      dwBrowserEvent: null
    } as DwGridEvent);
    this.onGridEvent({
      eventType: DwGridEventType.gridRowClick,
      eventData: row,
      browserEvent: event,
      dwBrowserEvent: null
    } as DwGridEvent);
  }

  public onRowKeydown(event: KeyboardEvent, row: UsersManageUsersCourseProgressGridQueryEntity): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.onRowClick(row, event);
  }

  public trackByCourseProgress(index: number, row: UsersManageUsersCourseProgressGridQueryEntity): string | number {
    return row.StudentCourseStudentCourseId || index;
  }

  protected mapResults(results: any[]): UsersManageUsersCourseProgressGridQueryEntity[] {
    const rows = (results || []) as UsersManageUsersCourseProgressGridQueryEntity[];
    const nextState = this.requestedPageInfo.pageNo === 1 ? {} : { ...this.rowStateById };

    rows.forEach(row => {
      nextState[row.StudentCourseStudentCourseId || 0] = this.buildRowState(row);
    });
    this.rowStateById = nextState;

    return rows;
  }

  protected getEffectiveSorts(): DwDataSort[] {
    if (!this.sortColumn) {
      return [];
    }

    return [{
      fieldName: this.sortColumn,
      ascending: this.sortDirection === 'asc'
    } as DwDataSort];
  }

  protected resolveInitialPageSize(): number {
    return 50;
  }

  private buildRowState(row: UsersManageUsersCourseProgressGridQueryEntity): CourseProgressGridRowState {
    const progressPercent = row.StudentCourseIsEndorsed
      ? 100
      : Math.max(0, Math.min(100, Math.round(row.StudentCoursePercentComplete || 0)));

    return {
      fullName: `${row.FirstName || ''} ${row.LastName || ''}`.trim()
        || row.UserDetailUserIdDisplay
        || row.UserName
        || '',
      mediaPath: row.DwroMediaMediaPath || row.UserPictureMediaIdDisplay || '',
      progressPercent,
      totalTimeLabel: this.formatDuration(row.StudentCourseTotalTimeSeconds),
      statusChips: [{
        label: row.StudentCourseProgressStatusProgressStatusName || 'Not Started',
        iconClass: this.getStatusIconClass(row.StudentCourseProgressStatusProgressStatusCssClass),
        statusClass: row.StudentCourseProgressStatusProgressStatusCssClass || 'progress-status--not-started'
      }]
    };
  }

  private resolveSortColumns(): void {
    this.sortColumns = {
      user: this.findSortField([
        'UserDetailUserIdDisplay',
        'UserDetail.UserIdDisplay',
        'FirstName',
        'dw_User.FirstName'
      ]),
      course: this.findSortField(['StudentCourseCourseIdDisplay', 'StudentCourse.CourseIdDisplay']),
      started: this.findSortField(['StudentCourseFirstActivityDateTime', 'StudentCourse.FirstActivityDateTime']),
      lastActivity: this.findSortField(['StudentCourseLastActivityDateTime', 'StudentCourse.LastActivityDateTime']),
      status: this.findSortField([
        'StudentCourseProgressStatusSortOrder',
        'StudentCourseProgressStatus.SortOrder'
      ]),
      totalTime: this.findSortField(['StudentCourseTotalTimeSeconds', 'StudentCourse.TotalTimeSeconds']),
      progress: this.findSortField(['StudentCoursePercentComplete', 'StudentCourse.PercentComplete']),
      acsScore: this.findSortField(['StudentCourseAcsTestLastScore', 'StudentCourse.AcsTestLastScore'])
    };

    if (!this.sortColumn || !Object.keys(this.sortColumns).some(key => this.sortColumns[key] === this.sortColumn)) {
      this.sortColumn = this.sortColumns.user || '';
      this.sortDirection = 'asc';
    }
  }

  private findSortField(candidateFields: string[]): string {
    const columns = this.gridConfig?.columnDefs || [];

    for (const candidateField of candidateFields) {
      const matchingColumn = columns.find(column =>
        column?.field === candidateField
        || column?.key === candidateField
        || column?.fullSortField === candidateField);

      if (matchingColumn) {
        return matchingColumn.fullSortField || matchingColumn.field || matchingColumn.key || '';
      }
    }

    return '';
  }

  private applyCurrentSort(): void {
    this.setSorts(this.getEffectiveSorts());
  }

  private getStatusIconClass(statusClass?: string): string {
    switch (statusClass) {
      case 'progress-status--complete':
        return 'fa fa-check';
      case 'progress-status--endorsed-waiting-acs':
        return 'fa fa-clock-o';
      case 'progress-status--acs-failed':
        return 'fa fa-times-circle';
      case 'progress-status--acs-passed':
        return 'fa fa-check-circle';
      default:
        return '';
    }
  }

  private formatDuration(totalSeconds?: number): string {
    const normalizedSeconds = Math.max(0, Math.round(totalSeconds || 0));
    if (!normalizedSeconds) {
      return '-';
    }

    const hours = Math.floor(normalizedSeconds / 3600);
    const minutes = Math.floor((normalizedSeconds % 3600) / 60);

    if (hours && minutes) {
      return `${hours}h ${minutes}m`;
    }

    return hours ? `${hours}h` : `${minutes}m`;
  }
}
